import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, BookMarked, Plus, Trash2, Copy, Save, Loader2, CheckCircle2, Ban, ShieldCheck,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LANG_OPTIONS = [
  ['en', 'Inglês'], ['fr', 'Francês'], ['es', 'Espanhol'],
  ['de', 'Alemão'], ['it', 'Italiano'], ['pt', 'Português'],
];

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const emptyTerm = () => ({ source: '', translations: {}, priority: 50, context: '', active: true, approved: false });

export default function GlossaryManager() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null); // full glossary being edited
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [dirty, setDirty] = useState(false);

  const loadList = useCallback(async () => {
    try {
      const r = await fetch(`${API}/glossaries`, { headers: authHeaders() });
      const data = await r.json().catch(() => []);
      if (!r.ok) throw new Error(data.detail || `Erro ${r.status}`);
      setList(data);
    } catch (e) { setErr(e.message); }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const open = async (id) => {
    setErr(''); setMsg('');
    const r = await fetch(`${API}/glossaries/${id}`, { headers: authHeaders() });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) { setErr(data.detail || `Erro ${r.status}`); return; }
    setSelected(data);
    setDirty(false);
  };

  const createNew = async () => {
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API}/glossaries`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ name: `Novo glossário ${list.length + 1}`, target_langs: ['en'], terms: [] }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || `Erro ${r.status}`);
      await loadList();
      setSelected(data); setDirty(false);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const duplicate = async (id) => {
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API}/glossaries/${id}/duplicate`, { method: 'POST', headers: authHeaders() });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || `Erro ${r.status}`);
      await loadList();
      setSelected(data); setDirty(false);
      setMsg('Glossário duplicado.');
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Eliminar este glossário? Os snapshots já aplicados a sessões são preservados.')) return;
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API}/glossaries/${id}`, { method: 'DELETE', headers: authHeaders() });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || `Erro ${r.status}`);
      if (selected?.id === id) setSelected(null);
      await loadList();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const save = async () => {
    setBusy(true); setErr(''); setMsg('');
    try {
      const r = await fetch(`${API}/glossaries/${selected.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          name: selected.name, description: selected.description,
          target_langs: selected.target_langs, terms: selected.terms,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || `Erro ${r.status}`);
      setSelected(data); setDirty(false);
      setMsg(`Guardado (versão ${data.version}). Sessões ativas mantêm o snapshot anterior até aplicar a atualização em pausa.`);
      await loadList();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const patch = (changes) => { setSelected((s) => ({ ...s, ...changes })); setDirty(true); };
  const patchTerm = (idx, changes) => {
    setSelected((s) => {
      const terms = s.terms.map((t, i) => (i === idx ? { ...t, ...changes } : t));
      return { ...s, terms };
    });
    setDirty(true);
  };
  const toggleLang = (l) => {
    patch({ target_langs: selected.target_langs.includes(l) ? selected.target_langs.filter((x) => x !== l) : [...selected.target_langs, l] });
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <header className="border-b border-slate-800 bg-slate-950/95 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/host')} className="text-slate-400 hover:text-white" data-testid="glossary-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <BookMarked className="w-5 h-5 text-amber-400" />
          <h1 className="font-['Manrope'] font-bold text-white text-sm">Glossários Reutilizáveis</h1>
          <span className="text-[10px] text-slate-500">Termos aprovados e ativos são aplicados às traduções (live e demo) via snapshot imutável por sessão.</span>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* List */}
        <div className="space-y-2" data-testid="glossary-list">
          <button onClick={createNew} disabled={busy} data-testid="glossary-create-btn"
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-amber-600 hover:bg-amber-700 disabled:opacity-40 text-white text-xs font-semibold py-2">
            <Plus className="w-3.5 h-3.5" /> Novo glossário
          </button>
          {list.map((g) => (
            <div key={g.id} onClick={() => open(g.id)} data-testid={`glossary-item-${g.id}`}
              className={`rounded-lg border p-3 cursor-pointer transition-colors ${selected?.id === g.id ? 'border-amber-500/60 bg-amber-500/10' : 'border-slate-800 bg-slate-900/70 hover:border-slate-600'}`}>
              <div className="text-sm text-white font-semibold truncate">{g.name}</div>
              <div className="text-[10px] text-slate-500">
                v{g.version} · {g.terms_count} termos ({g.approved_terms} aprovados) · {(g.target_langs || []).join(', ').toUpperCase()}
              </div>
            </div>
          ))}
          {list.length === 0 && <p className="text-xs text-slate-500 px-1">Sem glossários. Crie o primeiro.</p>}
        </div>

        {/* Editor */}
        <div className="lg:col-span-3 space-y-3">
          {err && <div className="rounded-lg border border-red-500/40 bg-red-500/10 text-red-300 text-xs px-4 py-2" data-testid="glossary-error">{err}</div>}
          {msg && <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-xs px-4 py-2" data-testid="glossary-msg">{msg}</div>}
          {!selected ? (
            <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-10 text-center text-slate-500 text-sm">
              Selecione ou crie um glossário para editar.
            </div>
          ) : (
            <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-4 space-y-4" data-testid="glossary-editor">
              <div className="flex flex-wrap items-center gap-2">
                <input value={selected.name} onChange={(e) => patch({ name: e.target.value })} data-testid="glossary-name-input"
                  className="flex-1 min-w-[200px] bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-white font-semibold focus:outline-none focus:border-amber-500/60" />
                <span className="text-[10px] text-slate-500">versão {selected.version}</span>
                <button onClick={() => duplicate(selected.id)} disabled={busy} data-testid="glossary-duplicate-btn"
                  className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-md border border-slate-700 text-slate-300 hover:bg-slate-800">
                  <Copy className="w-3 h-3" /> Duplicar
                </button>
                <button onClick={() => remove(selected.id)} disabled={busy} data-testid="glossary-delete-btn"
                  className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-md border border-red-600/50 text-red-300 hover:bg-red-600/15">
                  <Trash2 className="w-3 h-3" /> Eliminar
                </button>
                <button onClick={save} disabled={busy || !dirty} data-testid="glossary-save-btn"
                  className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-xs font-semibold px-4 py-2">
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Guardar
                </button>
              </div>
              <input value={selected.description || ''} onChange={(e) => patch({ description: e.target.value })}
                placeholder="Descrição (ex.: Estoril Open — jogadores, patrocinadores, terminologia de ténis)"
                data-testid="glossary-description-input"
                className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-xs text-slate-300 focus:outline-none focus:border-amber-500/60" />
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] text-slate-500">Idiomas de destino:</span>
                {LANG_OPTIONS.filter(([l]) => l !== selected.source_lang).map(([l, label]) => (
                  <button key={l} onClick={() => toggleLang(l)} data-testid={`glossary-lang-${l}`}
                    className={`text-[11px] px-2.5 py-1 rounded-full border ${selected.target_langs.includes(l) ? 'border-amber-500/60 bg-amber-500/15 text-amber-300' : 'border-slate-700 text-slate-400 hover:border-slate-500'}`}>
                    {label}
                  </button>
                ))}
              </div>

              {/* Terms */}
              <div className="space-y-2" data-testid="glossary-terms">
                {(selected.terms || []).map((t, idx) => (
                  <div key={t.id || idx} className={`rounded-lg border p-3 space-y-2 ${t.active ? 'border-slate-700 bg-slate-950/60' : 'border-slate-800 bg-slate-950/30 opacity-60'}`} data-testid={`glossary-term-${idx}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <input value={t.source} onChange={(e) => patchTerm(idx, { source: e.target.value })}
                        placeholder="Termo de origem" data-testid={`glossary-term-source-${idx}`}
                        className="flex-1 min-w-[160px] bg-slate-900 border border-slate-700 rounded-md px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-amber-500/60" />
                      <label className="flex items-center gap-1 text-[10px] text-slate-500">
                        Prioridade
                        <input type="number" min={0} max={100} value={t.priority}
                          onChange={(e) => patchTerm(idx, { priority: Number(e.target.value) })}
                          data-testid={`glossary-term-priority-${idx}`}
                          className="w-16 bg-slate-900 border border-slate-700 rounded-md px-2 py-1 text-xs text-white" />
                      </label>
                      <button onClick={() => patchTerm(idx, { active: !t.active })} data-testid={`glossary-term-active-${idx}`}
                        className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full border ${t.active ? 'border-sky-500/50 text-sky-300' : 'border-slate-700 text-slate-500'}`}>
                        <Ban className="w-3 h-3" /> {t.active ? 'Ativo' : 'Inativo'}
                      </button>
                      <button onClick={() => patchTerm(idx, { approved: !t.approved })} data-testid={`glossary-term-approved-${idx}`}
                        className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full border ${t.approved ? 'border-emerald-500/50 text-emerald-300' : 'border-amber-600/50 text-amber-400'}`}>
                        {t.approved ? <CheckCircle2 className="w-3 h-3" /> : <ShieldCheck className="w-3 h-3" />}
                        {t.approved ? 'Aprovado' : 'Por aprovar'}
                      </button>
                      <button onClick={() => { patch({ terms: selected.terms.filter((_, i) => i !== idx) }); }}
                        data-testid={`glossary-term-remove-${idx}`}
                        className="p-1 text-red-400 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {selected.target_langs.map((l) => (
                        <input key={l} value={t.translations?.[l] || ''}
                          onChange={(e) => patchTerm(idx, { translations: { ...t.translations, [l]: e.target.value } })}
                          placeholder={`Tradução ${l.toUpperCase()}`} data-testid={`glossary-term-tr-${idx}-${l}`}
                          className="bg-slate-900 border border-slate-700 rounded-md px-2.5 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-amber-500/60" />
                      ))}
                    </div>
                    <input value={t.context || ''} onChange={(e) => patchTerm(idx, { context: e.target.value })}
                      placeholder="Contexto opcional (ex.: torneio de ténis)" data-testid={`glossary-term-context-${idx}`}
                      className="w-full bg-slate-900 border border-slate-800 rounded-md px-2.5 py-1.5 text-[11px] text-slate-400 focus:outline-none focus:border-amber-500/60" />
                  </div>
                ))}
                <button onClick={() => { patch({ terms: [...(selected.terms || []), emptyTerm()] }); }}
                  data-testid="glossary-add-term-btn"
                  className="inline-flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300">
                  <Plus className="w-4 h-4" /> Adicionar termo
                </button>
              </div>
              <p className="text-[9px] text-slate-600">
                Apenas termos <span className="text-emerald-400">aprovados</span> e <span className="text-sky-400">ativos</span> entram nas traduções.
                As sessões usam um snapshot imutável — edições aqui só se aplicam após "Aplicar atualização" com a sessão em pausa.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
